import React from "react";
import { Helmet } from "react-helmet";
import { Layout } from "views/common/Layout";
import { allPosts } from "@content-collections/generated";
import { useParams } from "react-router-dom";

export const BlogPost = () => {
    const { slug } = useParams();
    const post = allPosts.find((post) => post._meta.path === slug);

    if (!post) {
        return (
            <Layout>
                <Helmet>
                    <title>Post Not Found | LaunchHero FYI Blog</title>
                    <meta name="robots" content="noindex, follow" />
                </Helmet>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                    <div className="mx-auto max-w-3xl text-center">
                        <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            Post Not Found
                        </h1>
                        <p className="mt-4 text-lg text-gray-600">
                            The blog post you're looking for doesn't exist.
                        </p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <Helmet>
                <title>{`${post.title} | LaunchHero FYI Blog`}</title>
                <meta name="description" content={post.summary} />
                <meta
                    name="keywords"
                    content={`${post.tags?.join(", ")}, ${
                        post.category
                    }, AI tools, product insights, technology trends`}
                />
                <meta
                    property="og:title"
                    content={`${post.title} | LaunchHero FYI Blog`}
                />
                <meta property="og:description" content={post.summary} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={post.image} />
                <meta property="article:published_time" content={post.date} />
                <meta property="article:author" content={post.author} />
                <meta property="article:section" content={post.category} />
                {post.tags?.map((tag, index) => (
                    <meta key={index} property="article:tag" content={tag} />
                ))}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={`${post.title} | LaunchHero FYI Blog`}
                />
                <meta name="twitter:description" content={post.summary} />
                <meta name="twitter:image" content={post.image} />
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div>
                    <article>
                        <div className="mx-auto max-w-3xl">
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>{post.category}</span>
                                <span>•</span>
                                <span>{post.date}</span>
                            </div>
                            <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                {post.title}
                            </h1>
                            {/*<div className="mt-6 flex items-center gap-4">
                                <div className="text-sm text-gray-500">
                                    <span className="font-medium text-gray-900">
                                        {post.author}
                                    </span>
                                    <span className="mx-2">•</span>
                                    <span>{post.date}</span>
                                </div>
                            </div>*/}
                            <div className="mt-8 aspect-video overflow-hidden rounded-lg">
                                <img
                                    crossOrigin={
                                        process.env.NODE_ENV === "development"
                                            ? "anonymous"
                                            : ""
                                    }
                                    src={post.image}
                                    alt={post.imageAlt}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {post.tags?.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                            <div className="mt-8 mb-8">
                                <div
                                    className="prose prose-lg max-w-none
                                        prose-headings:font-bold
                                        prose-h1:text-4xl prose-h1:mb-8
                                        prose-h2:text-3xl prose-h2:mt-12
                                        prose-h3:text-2xl prose-h3:mt-8
                                        prose-p:text-gray-700 prose-p:leading-8
                                        prose-li:text-gray-700
                                        prose-a:text-blue-600
                                        prose-blockquote:border-l-4 prose-blockquote:border-blue-400
                                        prose-code:bg-gray-100 prose-code:text-gray-800 prose-code:px-1.5 prose-code:py-0.5 
                                        prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:before:content-[''] prose-code:after:content-['']
                                        prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-6 prose-pre:rounded-lg
                                        prose-pre:overflow-x-auto prose-pre:my-8 prose-pre:font-mono prose-pre:shadow-lg
                                        prose-pre:border prose-pre:border-gray-800
                                        prose-pre:before:content-[''] prose-pre:after:content-['']
                                        prose-pre:code:bg-transparent prose-pre:code:text-gray-100 prose-pre:code:p-0
                                        prose-pre:code:border-0 prose-pre:code:rounded-none
                                        prose-img:rounded-lg
                                        prose-table:border-collapse prose-table:w-full prose-table:my-8
                                        prose-table:shadow-sm prose-table:rounded-lg prose-table:overflow-hidden
                                        prose-thead:bg-gray-50 prose-thead:border-b prose-thead:border-gray-200
                                        prose-th:border prose-th:border-gray-200 prose-th:bg-gray-50 prose-th:p-4 
                                        prose-th:text-left prose-th:text-sm prose-th:font-semibold prose-th:text-gray-800
                                        prose-tbody:divide-y prose-tbody:divide-gray-200
                                        prose-td:border prose-td:border-gray-200 prose-td:p-4 prose-td:text-gray-700
                                        prose-td:align-middle
                                        prose-tr:hover:bg-gray-50/50
                                        prose-tr:transition-colors prose-tr:duration-200"
                                    dangerouslySetInnerHTML={{
                                        __html: post.html,
                                    }}
                                />
                            </div>
                        </div>
                    </article>
                </div>
            </div>
        </Layout>
    );
};
